import React, { useState, useEffect, useContext } from "react";
import { Box, Checkbox } from "@mui/material";

import { apiGet } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { Utils } from "common/Utils";
import CurrencyFormatter from "components/Formatter/CurrencyFormatter";

import LoaderAnimation from "pages/LoaderAnimation";

export default function ComplianceCompare ({isActive, complianceId, setComplianceCompareComplete, isViewOnly, completedMessage}) {
    const { error } = useContext(PrivateRoutesContext);

    const [complianceDetails, setComplianceDetails] = useState(null);
    const [isLoadingComplianceDetails, setIsLoadingComplianceDetails] = useState(false);
    const [initialComplianceApprovals, setInitialComplianceApprovals] = useState([]);

    useEffect(() => {
        if (isActive || isViewOnly) {
            getComplianceDetails();
        }
    },[isActive, isViewOnly, complianceId])
    
    useEffect(() => {
        if (complianceDetails && complianceDetails.statistics) {
            setInitialComplianceApprovals(complianceDetails.statistics.map(r => !(r.doRequireApproval || r.overrideRequireApproval)))
        }
    },[complianceDetails]);

    useEffect(() => {
        var canComplete = !(initialComplianceApprovals && initialComplianceApprovals.length > 0 && initialComplianceApprovals.every(c => c));

        setComplianceCompareComplete(canComplete);
    },[initialComplianceApprovals])

    const getComplianceDetails = () => {
        setIsLoadingComplianceDetails(true);
        setComplianceDetails(null);

        apiGet(apiUrls.getInitialComplianceDetails(complianceId)).then(response => {
            if (response.data) {
                setComplianceDetails(response.data);
            }
        }).catch(response => {
            console.log(response, "error");
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred loading details.");
            error.setDoShowError(true);
        }).finally(() => {
            setIsLoadingComplianceDetails(false);
        });
    }

    return (
        <Box>
            <Box className="container ">
                {isLoadingComplianceDetails ?
                    <LoaderAnimation />
                : complianceDetails && complianceDetails.statistics.length > 0 ?
                <>
                    {complianceDetails.retroDate ? 
                    <Box className="row">
                        <Box className="col-2"></Box>
                        <Box className="col-8">
                            <strong>{`Retro Date: ${Utils.formatDate(complianceDetails.retroDate)}`}</strong>
                        </Box>
                        <Box className="col-2"></Box>
                    </Box> : null}
                    <Box className="row">
                        <Box className="col-3 border border-dark">
                        </Box>
                        <Box className="col-4 border border-dark text-center">
                            <strong>Current: {Utils.formatDate(complianceDetails.currentPeriod)}</strong>
                        </Box>
                        <Box className="col-4 border border-dark text-center">
                            <strong>
                                Prior Average{!Utils.isMinDate(complianceDetails.compareStart) && !Utils.isMinDate(complianceDetails.compareEnd) ? 
                                    complianceDetails.compareStart === complianceDetails.compareEnd ? `: ${Utils.formatDate(complianceDetails.compareStart)}` :
                                    `: ${Utils.formatDate(complianceDetails.compareStart)} - ${Utils.formatDate(complianceDetails.compareEnd)}` : 
                                    null
                                }
                            </strong>
                        </Box>
                        <Box className="col-1 border border-dark text-center">
                            <strong>Approve<sup>1</sup></strong>
                        </Box>
                    </Box>
                    <Box className="row">
                        <Box className="col-3 border border-dark text-center">
                            <strong>Name</strong>
                        </Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Total</strong>
                        </Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Retro</strong>
                        </Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Total</strong>
                        </Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Retro</strong>
                        </Box>
                        <Box className="col-1 border border-dark text-center"></Box>
                    </Box>
                    {complianceDetails.statistics.map((s,i) => {
                        var isInt = Number.isInteger(s.currentPeriod) &&
                            Number.isInteger(s.retroCurrentPeriod) &&
                            Number.isInteger(s.previousAverage) &&
                            Number.isInteger(s.retroPreviousAverage);

                        return (
                            <Box className="row" key={i}>
                                <Box className="col-3 border border-dark">
                                    {s.name}
                                </Box>
                                <Box className="col-2 border border-dark text-end">
                                    {s.isCurrency ? <CurrencyFormatter value={s.currentPeriod} /> : isInt ? s.currentPeriod : Utils.roundToTwo(s.currentPeriod)}
                                </Box>
                                <Box className="col-2 border border-dark text-end">
                                    {s.isCurrency ? <CurrencyFormatter value={s.retroCurrentPeriod} /> : isInt ? s.retroCurrentPeriod :  Utils.roundToTwo(s.retroCurrentPeriod)}
                                </Box>
                                <Box className="col-2 border border-dark text-end">
                                    {s.isCurrency ? <CurrencyFormatter value={s.previousAverage} /> : isInt ? s.previousAverage :  Utils.roundToTwo(s.previousAverage)}
                                </Box>
                                <Box className="col-2 border border-dark text-end">
                                    {s.isCurrency ? <CurrencyFormatter value={s.retroPreviousAverage} /> : isInt ? s.retroPreviousAverage :  Utils.roundToTwo(s.retroPreviousAverage)}
                                </Box>
                                <Box className="col-1 border border-dark text-center">
                                    {s.doRequireApproval || s.overrideRequireApproval ? 
                                        <Checkbox
                                            checked={initialComplianceApprovals[i]}
                                            onChange={() => setInitialComplianceApprovals((prevState) => {
                                                let s = [...prevState];
                                                s[i] = !s[i];
                                                return s;
                                            })}
                                        />
                                    : null}
                                </Box>
                            </Box>);
                    })}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2}}>
                        <strong><sup>1</sup>Note: All deviations above 10% require approval</strong>
                    </Box>
                    {completedMessage ? 
                        <Box sx={{ display: 'flex', justifyContent: 'center'}}>
                            <h3>{completedMessage}</h3>
                        </Box>
                    : null}
                </> : 
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    Unable to load compliance detials.
                </Box>
                }
            </Box>
        </Box>
    )
}